import './previous_appointment.scss';
import { CalendarTodayOutlined, TimerOutlined } from '@mui/icons-material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import {
  Alert,
  Container,
  Grid,
  LinearProgress,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Localization from 'l18n/l18n';
import Appointments from 'models/appointments';
import React, { memo,useCallback, useEffect, useRef, useState } from 'react';
import {Helmet} from 'react-helmet';
import { Controller, useForm } from 'react-hook-form';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate } from 'react-router-dom';
import ViewportList from 'react-viewport-list';

const dayjs = require('dayjs');

function Title(props) {
  return (
    <Typography component="h4" variant="h4" color="#000000">
      {props.children}
    </Typography>
  );
}

function PreviousAppointments() {
  const [previousAppointments, setPreviousAppointments] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [limitPerPage, setLimitPerPage] = useState(10);
  const [date, setDate] = useState(dayjs());
  const scrollContainerRef = useRef(null);
  const [error, setError] = useState(false);
  const [showEmptyAppointmentsAlert, setShowEmptyAppointmentsAlert] =
    useState(false);
  const [loading, setLoading] = useState(true);
  const { control, watch } = useForm({
    defaultValues: {
      appointment_calendar_date: date,
      searched_value: '',
    },
  });

  const navigate = useNavigate() ;

  const checkHasMore = (error) => {
    setHasMore(false);
    setLoading(false);
    if (error !== 10) {
      setError(true);
    }
  };
  const LightTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.common.white,
      color: 'rgba(0, 0, 0, 0.87)',
      boxShadow: theme.shadows[1],
      fontSize: 16,
     
    },
  }));
  const getData = useCallback(async () => {
    setShowEmptyAppointmentsAlert(false);
    Appointments.getPreviousAppointment({
      start_date: watch('appointment_calendar_date').format('YYYY-MM-DD'),
      page: pageNumber,
      limitPerPage,
    })
      .then(async (response) => {
        setPageNumber((prev) =>
          Math.ceil((previousAppointments.length + prev) / limitPerPage + 1)
        );
        const previousAppointmentsData = response.previous_appointments || [];

        if (previousAppointmentsData.length < 1 && previousAppointments.length == 0 ) {
          setShowEmptyAppointmentsAlert(true);
          setHasMore(false);
        }

        if (previousAppointmentsData.length > 0 &&  previousAppointmentsData.length < limitPerPage) {
          setHasMore(false);
        }

        if(previousAppointmentsData.length< 1){
          setHasMore(false);
        }

        setPreviousAppointments(previousAppointments.concat(previousAppointmentsData));
        setLoading(false);
      })
      .catch((err) => checkHasMore(err));
  }, [pageNumber, limitPerPage, previousAppointments, setLoading]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      //add search_value , when enabling search
      if (name == 'appointment_calendar_date') {
        const { appointment_calendar_date } = value;
        setDate(appointment_calendar_date);
        setPreviousAppointments([]);
        setError(false);
        setHasMore(true);
        setPageNumber(1);
        setLoading(true);
        getData();
      }

      return () => subscription.unsubscribe();
    });
  }, [watch]);

  useEffect(() => {
    getData();
  }, []);
  const errorMessage =
    !loading && error ? (
      <Stack sx={{ width: '100%' }} spacing={2}>
        <Alert severity="error">{Localization.translate['error']}</Alert>
      </Stack>
    ) : (
      ''
    );
  const EmptyMessage =
    showEmptyAppointmentsAlert && !loading ? (
      <Stack sx={{ width: '100%' }} spacing={2}>
        <Alert severity="warning">
          {Localization.translate['empty_previous_appointments_alert']}
        </Alert>
      </Stack>
    ) : (
      ''
    );
  const AppointmentsList = (
    <InfiniteScroll
      next={() => getData(true)}
      hasMore={hasMore}
      dataLength={pageNumber * limitPerPage}
      loader={<LinearProgress />}
    >
      <ViewportList
        viewportRef={scrollContainerRef}
        items={previousAppointments}
        itemMinSize={40}
        margin={8}
      >
        {(appointment, key) => {
          const {
            patient_info_first_name,
            patient_info_second_name,
            patient_info_last_name,
            appointment_calendar_time_of_day,
            appointment_calendar_patient_comments,
            appointment_calendar_id
          } = appointment;

          const convertTimeTo12Hour = dayjs(
            appointment_calendar_time_of_day,
            'HH:MM:SS'
          ).format('hh:mm a');
 
          return (
            <TimelineItem
              key={key}
              className={'previous_appointments_timeline'}
              position="right"
              onClick={()=>navigate(`/appointment/${appointment_calendar_id}`)}
              sx={{cursor: 'pointer'}}
            >
              <TimelineSeparator className="timeline-separator">
                <TimelineDot color="primary" />
                <TimelineConnector sx={{ backgroundColor: 'transparent' }} />
              </TimelineSeparator>
              <TimelineContent sx={{ padding: 0 }}>
                <Grid container className="timeline-item">
                  <Grid container item xs={12} md={4}>
                    <PersonOutlineIcon
                      style={{ color: '#BDBDBD', marginLeft: 10 }}
                    />
                    <div>
                      {patient_info_first_name} {patient_info_second_name}{' '}
                      {patient_info_last_name}
                    </div>
                  </Grid>
                  <Grid container item xs={12} md={4}>
                    <TimerOutlined
                      style={{ color: '#BDBDBD', marginLeft: 10 }}
                    />
                    <div dir="ltr">{convertTimeTo12Hour}</div>
                  </Grid>
                  <Grid container item xs={12} md={4}>
                    <LightTooltip title={appointment_calendar_patient_comments}  placement="left">
                      <Typography variant="inherit" noWrap component="div"  sx={{display:'flex',maxWidth:'10rem'}}> 
                        <DescriptionOutlinedIcon  style={{ color: '#BDBDBD', marginLeft: 10 }} />
                        <div style={{width:'5rem',textOverflow:'ellipsis',overflow:'hidden'}}>
                        {appointment_calendar_patient_comments}
                        </div>
                      </Typography>
                    </LightTooltip>
                  </Grid>
                </Grid>
              </TimelineContent>
            </TimelineItem>
          );
        }}
      </ViewportList>
    </InfiniteScroll>
  );

  return (
    <> 
    <Helmet>
    <title>{`${Localization.translate['app_name']} | ${Localization.translate['previous_appointments']}`}</title>
    </Helmet>

    <Container maxWidth="lg">
      <Grid container rowSpacing={4}>
        <Grid item container spacing={4}>
          <Grid xs={12} lg={8} container item spacing={4}>
            <Grid item xs={9}>
              <Title>{Localization.translate['previous_appointments']} </Title>
            </Grid>
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Controller
                  name={'appointment_calendar_date'}
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <DatePicker
                    sx={{color:'red'}}
                    components={{
                      OpenPickerIcon: CalendarTodayOutlined
                    }}
                      label={Localization.translate['choose_date']}
                      value={value}
                      onChange={onChange}
                      renderInput={(params) => (
                        <TextField  focused  
                        sx={{ backgroundColor: '#ffffff',  
                        svg: { color:'#279BFF' },
                        }}dir="ltr" {...params}/>)}/>
                      )}/>
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Grid>
        <Grid xs={12} item container>
          <Grid item xs={12}>
            {EmptyMessage}
            {errorMessage}
          </Grid>
          <Grid item xs={12} lg={8} ref={scrollContainerRef}>
            <Timeline position="right"  >
              <TimelineItem className="previous_appointments_timeline">
                <TimelineSeparator style={{ marginRight: '-0.7rem' }}>
                  <TimelineDot sx={{ backgroundColor: '#DEF0FF' }}>
                    <ScheduleOutlinedIcon
                      sx={{ backgroundColor: '#DEF0FF', color: '#279BFF' }}
                      />
                  </TimelineDot>
                  <TimelineConnector sx={{ backgroundColor: '#DEF0FF' }} />
                </TimelineSeparator>
                <TimelineContent sx={{ py: '12px', px: 2 }}>
                  <Typography>
                    {dayjs(watch('appointment_calendar_date')).format(
                      'YYYY-MM-DD'
                      )}
                  </Typography>
                </TimelineContent>
              </TimelineItem>

              {!loading ? AppointmentsList : <LinearProgress />}
            </Timeline>
          </Grid>
        </Grid>
      </Grid>
    </Container>
                      </>
  );
}

export default memo(PreviousAppointments);
